/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useTimeConditionList.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Time Conditions list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { fetchTimeConditions } from '../api/timeConditionApi';
import { TimeCondition } from '../types';

interface UseTimeConditionListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing Time Conditions list
 * Implements Domain Contextual Filtering pattern
 */
export function useTimeConditionList(options: UseTimeConditionListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchTimeConditionsData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchTimeConditions(
          {
            page: params.page,
            limit: params.limit,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            domainId: effectiveDomainId,
          },
          params.search,
          params.sortBy,
        );

        return {
          data: result.data as TimeCondition[],
          pagination: result.pagination,
        };
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('timeConditions.messages.loadError');
        showError(message);
        return {
          data: [] as TimeCondition[],
          pagination: {
            page: params.page,
            limit: params.limit,
            total: 0,
            totalPages: 0,
          },
        };
      }
    },
    [showError, t, effectiveDomainId],
  );

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('timeConditions.columns.name'),
        visible: true,
        sortable: true,
      },
      {
        key: 'description',
        label: t('timeConditions.columns.description'),
        visible: true,
        sortable: false,
      },
      {
        key: 'timeGroup',
        label: t('timeConditions.columns.timeGroup'),
        visible: true,
        sortable: false,
      },
      {
        key: 'destinations',
        label: t('timeConditions.columns.destinations'),
        visible: true,
        sortable: false,
      },
      {
        key: 'status',
        label: t('timeConditions.columns.status'),
        visible: true,
        sortable: true,
      },
      {
        key: 'createdAt',
        label: t('common:createdAt'),
        visible: false,
        sortable: true,
      },
      {
        key: 'actions',
        label: t('common:actions'),
        visible: true,
        sortable: false,
      },
    ],
    [t],
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData: fetchTimeConditionsData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'time-conditions',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as TimeCondition[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
  };
}

